import { ComicPageLoader } from "@vapor/react-contrib-tax-compliance";
import { useGetSubscriptions } from "../../core/api/use-get-subscriptions/useGetSubscriptions";
import { AvailableServices } from "../../core/api/use-get-subscriptions/SubscriptionDto";
import { useTenantWorkspace } from "../../core/hooks/useTenantWorkspace";
import { WorkspaceIdProvider } from "../../core/context/WorkspaceIdProvider";
import { PermissionsGate } from "../../core/permissions/PermissionsGate";
import { PermissionsProvider } from "../../core/context/PermissionsProvider";
import { sessionStoreSlice } from "../../store/sessionStoreSlice";
import { ServiceNotActive } from "../components/service-not-active/ServiceNotActive";
import { WorkspaceNotAvailable } from "../components/workspace-not-available/WorkspaceNotAvailable";

interface ViewWrapperProps {
    children: React.ReactNode;
}

export const ViewWrapper: React.FC<ViewWrapperProps> = ({
    children,
}): React.JSX.Element => {
    // TENANT workspace (the current organization's own), not the workspace
    // selected in the shell: see useTenantWorkspace.
    const { workspace, isLoading: isLoadingWorkspace } = useTenantWorkspace();
    const { data: subscriptions, isFetching: isLoadingSubscriptions } =
        useGetSubscriptions(workspace?.id);

    const activeSubscription = !!subscriptions.find(
        (subscription) =>
            subscription.serviceId === AvailableServices.Fulfilment &&
            subscription.isActive === true,
    );

    if (activeSubscription)
        sessionStoreSlice.updateValues({ subscriptions: subscriptions });

    if (isLoadingWorkspace) return <ComicPageLoader loading={true} />;

    // Loading is over and there is still no tenant workspace: the list failed,
    // or the organization has none. Either way the app has nothing to operate
    // on, so it says so instead of hanging on the loader forever.
    if (!workspace) return <WorkspaceNotAvailable />;

    return (
        // The workspace is resolved here; passing it by context lets every hook
        // in the subtree read it on its first render, including views mounted
        // later (navigation between routes).
        <WorkspaceIdProvider workspaceId={workspace.id}>
            {/* Mounted as soon as the workspace is known, ABOVE the
                subscriptions loader: the policy roundtrip overlaps that fetch
                instead of queueing behind it. The cost is that policies are
                evaluated for an inactive subscription too. */}
            <PermissionsProvider>
                <ComicPageLoader loading={isLoadingSubscriptions}>
                    {activeSubscription ? (
                        <PermissionsGate>{children}</PermissionsGate>
                    ) : (
                        <ServiceNotActive />
                    )}
                </ComicPageLoader>
            </PermissionsProvider>
        </WorkspaceIdProvider>
    );
};
